/**
 * Plattform-Admin: Schulträger als Organigramm-Karten. Links der Haupt-Trägeradmin mit
 * seiner Stammschule, rechts der Baum der Schulen (Stammschule als Wurzel). Das Detail
 * zeigt Schulen (mit Trennen), Träger-User mit Freigaben und das Protokoll. Fehlt der
 * Haupt-Trägeradmin, lässt er sich hier neu setzen.
 */
import { api } from "../api";
import { openModal, closeModal, modalBody, openModal2, closeModal2, modalBody2 } from "../components/modal";
import { confirmDialog } from "../components/confirm-dialog";
import { showToast } from "../utils/toast";
import type { AdminCarrier, AdminCarrierDetail, AdminCarrierSchool, AdminModuleState } from "../types";
import { esc, formatDate } from "./traeger-shared";

let carriers: AdminCarrier[] = [];
let search = "";
let pageEl: HTMLElement | null = null;

const MODULE_LABELS: Record<string, string> = { planung: "Planung", schuelerPortal: "Schüler-Portal", stundenplan: "Stundenplan" };
const STATE_LABELS: Record<AdminModuleState, string> = { aktiv: "aktiv", test: "Testphase", aus: "nicht freigeschaltet" };

const ACTION_LABELS: Record<string, string> = {
  gegruendet: "Träger erstellt",
  verbunden: "Schule verbunden",
  anfrage_gestellt: "Anfrage zum Verbinden",
  anfrage_abgelehnt: "Anfrage abgelehnt",
  anfrage_zurueckgezogen: "Anfrage zurückgezogen",
  schule_angelegt: "Schule angelegt",
  getrennt: "Schule getrennt",
  betreten: "Schule betreten",
  user_hinzugefuegt: "Träger-User hinzugefügt",
  user_entfernt: "Träger-User entfernt",
  freigabe_geaendert: "Freigaben geändert",
  code_neu: "Neuer Code erzeugt",
  haupt_admin_gesetzt: "Haupt-Trägeradmin neu gesetzt",
  limit_geaendert: "Schullimit geändert",
  geloescht: "Träger gelöscht",
};

// ── Suche ──

function matches(c: AdminCarrier, q: string): boolean {
  if (!q) return true;
  const hay = [c.name, c.code, c.founder?.name, c.founder?.email, c.foundingSchool.name, ...c.schools.map(s => s.name)];
  return hay.some(h => (h || "").toLowerCase().includes(q));
}

/** Text escapen und Treffer der Suche mit <mark> hervorheben. */
function hl(text: string | null | undefined): string {
  const t = String(text ?? "");
  if (!search) return esc(t);
  const lower = t.toLowerCase();
  let out = "";
  let i = 0;
  for (;;) {
    const j = lower.indexOf(search, i);
    if (j < 0) break;
    out += esc(t.slice(i, j)) + `<mark>${esc(t.slice(j, j + search.length))}</mark>`;
    i = j + search.length;
  }
  return out + esc(t.slice(i));
}

// ── Bausteine ──

const schoolsLabel = (n: number) => n === 1 ? "1 Schule" : `${n} Schulen`;
const limitLabel = (n: number | null) => n === null ? "unbegrenzt" : String(n);

/** „3 Schulen“ oder mit Limit „3 von 5 Schulen“, beim Überschreiten in Warnfarbe. */
function schoolCountLabel(c: AdminCarrier): string {
  const n = c.schools.length;
  if (c.maxSchools === null) return schoolsLabel(n);
  const text = `${n} von ${schoolsLabel(c.maxSchools)}`;
  return n > c.maxSchools ? `<span style="color:var(--amber-800)" title="Limit überschritten">${text}</span>` : text;
}

function initials(name: string | null | undefined): string {
  const parts = String(name || "?").trim().split(/\s+/);
  return ((parts[0]?.[0] || "?") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

function dots(s: AdminCarrierSchool): string {
  return `<span class="carrier-dots">${(["planung", "schuelerPortal", "stundenplan"] as const).map(m =>
    `<span class="carrier-dot carrier-dot--${s.modules[m]}" title="${esc(MODULE_LABELS[m])}: ${esc(STATE_LABELS[s.modules[m]])}"></span>`).join("")}</span>`;
}

function schoolNode(s: AdminCarrierSchool, opts: { withDetach?: boolean } = {}): string {
  const meta = [
    s.ort ? esc(s.ort) : "",
    s.memberCount > 0 ? `${s.memberCount} Träger-User` : "",
    s.connectedAt && !s.isFoundingSchool ? `verbunden seit ${formatDate(s.connectedAt)}` : "",
  ].filter(Boolean).join(" · ");
  return `
    <div class="carrier-tree-label">
      <div>
        <div class="carrier-tree-name">${s.isFoundingSchool ? `<span class="carrier-tree-star" title="Stammschule">★</span>` : ""}${hl(s.name)}${s.isFoundingSchool ? ` <span class="badge badge-purple">Stammschule</span>` : ""}</div>
        ${meta ? `<div class="carrier-tree-meta">${meta}</div>` : ""}
      </div>
      <div style="display:flex;align-items:center;gap:.75rem">
        ${dots(s)}
        ${opts.withDetach && !s.isFoundingSchool ? `<button class="btn btn-sm" data-detach="${s.id}">Vom Träger trennen</button>` : ""}
      </div>
    </div>`;
}

/** Baum: Stammschule als Wurzel, die übrigen Schulen als Äste. */
function tree(c: AdminCarrier, opts: { withDetach?: boolean } = {}): string {
  const root = c.schools.find(s => s.isFoundingSchool);
  const branches = c.schools.filter(s => !s.isFoundingSchool);
  return `
    <div class="carrier-tree">
      ${root ? `<div class="carrier-tree-root">${schoolNode(root, opts)}</div>` : ""}
      ${branches.length ? `<ul class="carrier-tree-branches">${branches.map(s => `<li class="carrier-tree-node">${schoolNode(s, opts)}</li>`).join("")}</ul>`
        : `<p class="text-muted" style="font-size:.8125rem;margin:.75rem 0 0 1.25rem">Noch keine weiteren Schulen verbunden.</p>`}
    </div>`;
}

function personCard(c: AdminCarrier): string {
  if (!c.founder) {
    return `
      <div class="carrier-org-person carrier-org-person--missing">
        <div class="carrier-avatar">?</div>
        <div>
          <div style="font-weight:600">${c.founderMissing ? "Haupt-Trägeradmin nicht gefunden" : "Haupt-Trägeradmin nicht ladbar"}</div>
          <div class="text-muted" style="font-size:.8125rem">aus: ${hl(c.foundingSchool.name)} <span class="carrier-tree-star">★</span></div>
          ${c.founderMissing ? `<button class="btn btn-sm btn-primary" style="margin-top:.5rem" data-set-founder="${c.id}">Neu setzen</button>` : ""}
        </div>
      </div>`;
  }
  return `
    <div class="carrier-org-person">
      <div class="carrier-avatar">${esc(initials(c.founder.name))}</div>
      <div style="min-width:0">
        <div style="font-weight:600">${hl(c.founder.name)}</div>
        <div style="font-size:.8125rem;overflow:hidden;text-overflow:ellipsis"><a href="mailto:${esc(c.founder.email)}">${hl(c.founder.email)}</a></div>
        <div style="margin-top:.375rem"><span class="badge badge-purple">Haupt-Trägeradmin</span></div>
        <div class="text-muted" style="font-size:.8125rem;margin-top:.375rem">aus: ${hl(c.foundingSchool.name)} <span class="carrier-tree-star">★</span></div>
      </div>
    </div>`;
}

function card(c: AdminCarrier): string {
  return `
    <div class="card carrier-org-card${c.founderMissing ? " carrier-org-card--warn" : ""}">
      <div class="carrier-org-head">
        <div>
          <h2>${hl(c.name)}</h2>
          <div class="text-muted" style="font-size:.8125rem;margin-top:.125rem">
            <code class="carrier-org-code">${hl(c.code)}</code> · seit ${formatDate(c.createdAt)}
          </div>
        </div>
        <div class="text-muted" style="font-size:.8125rem;text-align:right">
          ${schoolCountLabel(c)} · ${c.memberCount === 1 ? "1 Träger-User" : `${c.memberCount} Träger-User`}
        </div>
      </div>
      ${c.founderMissing ? `<div class="carrier-org-warn">Der Haupt-Trägeradmin ist nicht mehr als Benutzer der Stammschule vorhanden. Niemand kann den Träger verwalten, bis ein neuer gesetzt ist.</div>` : ""}
      <div class="carrier-org-body">
        ${personCard(c)}
        ${tree(c)}
      </div>
      <div class="carrier-org-foot"><button class="btn btn-sm btn-secondary" data-details="${c.id}">Details</button></div>
    </div>`;
}

function renderList() {
  const box = pageEl!.querySelector("#tr-admin-list")!;
  const visible = carriers
    .filter(c => matches(c, search))
    .sort((a, b) => Number(b.founderMissing) - Number(a.founderMissing) || a.name.localeCompare(b.name, "de"));
  if (carriers.length === 0) {
    box.innerHTML = `<div class="card empty-state" style="padding:2rem"><p>Noch kein Schulträger angelegt.</p></div>`;
    return;
  }
  if (visible.length === 0) {
    box.innerHTML = `<div class="card empty-state" style="padding:2rem"><p>Kein Schulträger passt zur Suche.</p></div>`;
    return;
  }
  box.innerHTML = visible.map(card).join("");
}

async function reload() {
  carriers = await api.admin.carriers.list();
  renderList();
}

// ── Aktionen ──

async function detachSchool(c: AdminCarrier, schoolId: number, after: () => Promise<void>) {
  const s = c.schools.find(x => x.id === schoolId);
  if (!s) return;
  const ok = await confirmDialog({
    title: "Schule vom Schulträger trennen",
    message: `„${s.name}“ wird vom Schulträger „${c.name}“ getrennt. Die Daten der Schule bleiben erhalten.\n\nTräger-User dieser Schule verlieren ihren Zugang zum Schulträger, und Freigaben auf diese Schule entfallen.`,
    confirmLabel: "Trennen",
    danger: true,
  });
  if (!ok) return;
  try {
    await api.admin.carriers.detachSchool(c.id, schoolId);
    showToast("Schule getrennt");
    await after();
  } catch (err: any) { alert("Fehler: " + err.message); }
}

async function openSetFounder(c: AdminCarrier, after: () => Promise<void>) {
  let admins: { id: string; firstName: string; lastName: string; email: string }[] = [];
  try {
    admins = (await api.admin.schools.users.list(c.foundingSchool.id)).filter(u => u.role === "schul-admin" && u.enabled);
  } catch (err: any) { alert("Fehler: " + err.message); return; }
  const open = openModal2;
  if (admins.length === 0) {
    open("Haupt-Trägeradmin neu setzen", `
      <p style="font-size:.875rem;color:var(--gray-600)">Die Stammschule „${esc(c.foundingSchool.name)}“ hat keinen aktiven Schul-Admin. Lege dort zuerst einen an (Mandantenverwaltung, Benutzer).</p>
      <div class="form-actions"><button class="btn btn-primary" id="sf-close">OK</button></div>`);
    modalBody2().querySelector("#sf-close")!.addEventListener("click", closeModal2);
    return;
  }
  open("Haupt-Trägeradmin neu setzen", `
    <p style="font-size:.875rem;color:var(--gray-600);margin-top:0">
      Der neue Haupt-Trägeradmin muss Schul-Admin der Stammschule „${esc(c.foundingSchool.name)}“ sein.
      Er erhält Zugriff auf alle Schulen des Trägers und kann ihn verwalten.
      ${c.founder ? `Der bisherige Haupt-Trägeradmin ${esc(c.founder.name)} verliert diese Rolle.` : ""}
    </p>
    <div class="form-group"><label>Schul-Admin der Stammschule</label><select id="sf-user">
      ${admins.map(u => `<option value="${esc(u.id)}"${u.id === c.founder?.userId ? " disabled" : ""}>${esc(`${u.firstName} ${u.lastName}`.trim() || u.email)} (${esc(u.email)})${u.id === c.founder?.userId ? ", bisher" : ""}</option>`).join("")}
    </select></div>
    <div id="sf-error" style="color:var(--danger,#dc2626);font-size:.875rem;min-height:1.2rem"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="sf-cancel">Abbrechen</button><button class="btn btn-primary" id="sf-ok">Neu setzen</button></div>`);
  const mb = modalBody2();
  const sel = mb.querySelector("#sf-user") as HTMLSelectElement;
  const firstFree = [...sel.options].find(o => !o.disabled);
  if (firstFree) sel.value = firstFree.value;
  mb.querySelector("#sf-cancel")!.addEventListener("click", closeModal2);
  mb.querySelector("#sf-ok")!.addEventListener("click", async () => {
    const errorEl = mb.querySelector("#sf-error") as HTMLElement;
    if (!sel.value || sel.selectedOptions[0]?.disabled) { errorEl.textContent = "Bitte einen anderen Schul-Admin wählen."; return; }
    try {
      await api.admin.carriers.setFounder(c.id, sel.value);
      closeModal2();
      showToast("Haupt-Trägeradmin gesetzt");
      await after();
    } catch (err: any) { errorEl.textContent = err.message; }
  });
}

// ── Detail ──

type Tab = "schulen" | "benutzer" | "protokoll" | "optionen";

async function openDetails(carrierId: number, tab: Tab = "schulen", showEnter = false) {
  const c = carriers.find(x => x.id === carrierId);
  if (!c) return;
  let detail: AdminCarrierDetail;
  try { detail = await api.admin.carriers.get(carrierId, showEnter); }
  catch (err: any) { alert("Fehler: " + err.message); return; }

  const refresh = async (nextTab: Tab) => {
    await reload();
    if (carriers.some(x => x.id === carrierId)) await openDetails(carrierId, nextTab, showEnter);
    else closeModal();
  };

  openModal(c.name, `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:1rem;margin-bottom:.75rem;flex-wrap:wrap">
      <div class="text-muted" style="font-size:.8125rem"><code class="carrier-org-code">${esc(c.code)}</code> · seit ${formatDate(c.createdAt)} ·
        Haupt-Trägeradmin: ${c.founder ? `${esc(c.founder.name)} (${esc(c.founder.email)})` : `<span style="color:var(--danger,#dc2626)">nicht gefunden</span>`}</div>
      <button class="btn btn-sm" id="td-set-founder">Haupt-Trägeradmin neu setzen</button>
    </div>
    <div class="detail-tabs">
      <button class="detail-tab${tab === "schulen" ? " active" : ""}" data-tab="schulen">Schulen (${c.schools.length})</button>
      <button class="detail-tab${tab === "benutzer" ? " active" : ""}" data-tab="benutzer">Träger-User (${detail.users.length})</button>
      <button class="detail-tab${tab === "protokoll" ? " active" : ""}" data-tab="protokoll">Protokoll</button>
      <button class="detail-tab${tab === "optionen" ? " active" : ""}" data-tab="optionen">Optionen</button>
    </div>
    <div class="tab-content-wrap">
      <div class="tab-pane${tab === "schulen" ? " active" : ""}" data-pane="schulen">${tree(c, { withDetach: true })}</div>
      <div class="tab-pane${tab === "benutzer" ? " active" : ""}" data-pane="benutzer">${usersMatrix(detail)}</div>
      <div class="tab-pane${tab === "protokoll" ? " active" : ""}" data-pane="protokoll">
        <label style="display:flex;align-items:center;gap:.5rem;font-size:.875rem;margin-bottom:.75rem">
          <input type="checkbox" id="td-enter" ${showEnter ? "checked" : ""} /> „Schule betreten“ anzeigen
        </label>
        ${logTable(detail)}
      </div>
      <div class="tab-pane${tab === "optionen" ? " active" : ""}" data-pane="optionen">${optionsForm(c)}</div>
    </div>`, { wide: true });

  const mb = modalBody();
  mb.querySelectorAll<HTMLButtonElement>(".detail-tab").forEach(btn => btn.addEventListener("click", () => {
    mb.querySelectorAll(".detail-tab").forEach(b => b.classList.toggle("active", b === btn));
    mb.querySelectorAll<HTMLElement>(".tab-pane").forEach(p => p.classList.toggle("active", p.dataset.pane === btn.dataset.tab));
  }));
  mb.querySelector("#td-enter")!.addEventListener("change", (e) =>
    openDetails(carrierId, "protokoll", (e.target as HTMLInputElement).checked));
  mb.querySelector("#td-set-founder")!.addEventListener("click", () => openSetFounder(c, () => refresh("schulen")));
  mb.querySelectorAll<HTMLButtonElement>("[data-detach]").forEach(btn => btn.addEventListener("click", () =>
    detachSchool(c, Number(btn.dataset.detach), () => refresh("schulen"))));
  mb.querySelector("#to-save")!.addEventListener("click", () => saveOptions(c, mb, () => refresh("optionen")));
}

function optionsForm(c: AdminCarrier): string {
  const n = c.schools.length;
  const over = c.maxSchools !== null && n > c.maxSchools;
  return `
    <div class="form-group" style="max-width:320px">
      <label for="to-max">Maximale Anzahl Schulen</label>
      <input id="to-max" type="number" min="1" max="999" step="1" placeholder="unbegrenzt" value="${c.maxSchools ?? ""}" />
    </div>
    <p class="text-muted" style="font-size:.8125rem;margin:.25rem 0 .75rem">
      Leer lassen für unbegrenzt. Aktuell verbunden: ${schoolsLabel(n)} (inkl. Stammschule).
      Ist das Limit erreicht, kann der Schulträger keine Schulen mehr anlegen und keine Anfragen annehmen.
    </p>
    ${over ? `<div class="alert alert-warning" style="margin-bottom:.75rem">Limit überschritten. Bestehende Schulen bleiben verbunden, neue können erst wieder dazukommen, wenn weniger als ${schoolsLabel(c.maxSchools!)} verbunden sind.</div>` : ""}
    <div id="to-error" style="color:var(--danger,#dc2626);font-size:.875rem;min-height:1.2rem"></div>
    <div class="form-actions" style="justify-content:flex-start"><button class="btn btn-primary" id="to-save">Speichern</button></div>`;
}

async function saveOptions(c: AdminCarrier, mb: HTMLElement, after: () => Promise<void>) {
  const input = mb.querySelector("#to-max") as HTMLInputElement;
  const errorEl = mb.querySelector("#to-error") as HTMLElement;
  const raw = input.value.trim();
  const maxSchools = raw === "" ? null : Number(raw);
  if (maxSchools !== null && !(Number.isInteger(maxSchools) && maxSchools >= 1 && maxSchools <= 999)) {
    errorEl.textContent = "Bitte eine ganze Zahl von 1 bis 999 eingeben oder das Feld leer lassen.";
    return;
  }
  try {
    await api.admin.carriers.setOptions(c.id, { maxSchools });
    showToast("Optionen gespeichert");
    await after();
  } catch (err: any) { errorEl.textContent = err.message; }
}

function usersMatrix(d: AdminCarrierDetail): string {
  if (d.users.length === 0) return `<p class="text-muted" style="font-size:.875rem">Keine Träger-User.</p>`;
  return `
    <div class="table-wrap"><table class="carrier-matrix">
      <thead><tr><th>Benutzer</th><th>Eigene Schule</th>${d.schools.map(s => `<th class="carrier-matrix-school">${esc(s.name)}</th>`).join("")}</tr></thead>
      <tbody>
        ${d.users.map(u => `<tr>
          <td>
            <div style="font-weight:500">${esc(u.name ?? "Unbekannter Benutzer")}${u.isFounder ? ` <span class="badge badge-purple">Haupt-Trägeradmin</span>` : ""}</div>
            <div class="text-muted" style="font-size:.8125rem">${esc(u.email ?? "")}</div>
          </td>
          <td>${esc(u.homeSchoolName ?? "")}</td>
          ${d.schools.map(s => {
            const own = s.id === u.homeSchoolId;
            const granted = u.isFounder || u.schoolIds.includes(s.id);
            return `<td class="carrier-matrix-cell">${own ? `<span title="Eigene Schule" style="color:var(--gray-400)">●</span>` : granted ? `<span title="Freigegeben" style="color:var(--primary);font-weight:700">✓</span>` : ""}</td>`;
          }).join("")}
        </tr>`).join("")}
      </tbody>
    </table></div>
    <p class="text-muted" style="font-size:.8125rem;margin-top:.5rem">● eigene Schule, ✓ freigegeben (Arbeit als Schul-Admin). Der Haupt-Trägeradmin hat alle Schulen.</p>`;
}

function logDetails(e: { action: string; details: Record<string, any> | null }): string {
  const d = e.details || {};
  if (e.action === "gegruendet" && d.name) return `Name: ${esc(d.name)}`;
  if (e.action === "schule_angelegt" && d.name) return `Name: ${esc(d.name)}`;
  if (e.action === "getrennt") return d.durch === "plattform-admin" ? "durch Plattform-Admin" : d.durch === "traeger" ? "durch den Träger" : d.durch === "schule" ? "durch die Schule" : "";
  if (e.action === "freigabe_geaendert" || e.action === "user_hinzugefuegt") return Array.isArray(d.schoolIds) ? `${d.schoolIds.length} Schule(n) freigegeben` : "";
  if (e.action === "haupt_admin_gesetzt") return "durch Plattform-Admin";
  if (e.action === "limit_geaendert") return `von ${esc(limitLabel(d.alt ?? null))} auf ${esc(limitLabel(d.neu ?? null))}`;
  if (e.action === "anfrage_gestellt" && d.einwilligung) return "mit Einwilligung der Schule";
  if (e.action === "verbunden" && d.anfrageVon) return "Anfrage angenommen";
  return "";
}

function logTable(d: AdminCarrierDetail): string {
  if (d.log.length === 0) return `<p class="text-muted" style="font-size:.875rem">Keine Einträge.</p>`;
  return `
    <div class="table-wrap"><table>
      <thead><tr><th>Zeit</th><th>Benutzer</th><th>Aktion</th><th>Schule</th><th>Details</th></tr></thead>
      <tbody>
        ${d.log.map(e => `<tr>
          <td style="white-space:nowrap">${esc(new Date(e.createdAt).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }))}</td>
          <td>${esc(e.userName ?? "")}</td>
          <td>${esc(ACTION_LABELS[e.action] ?? e.action)}</td>
          <td>${esc(e.schoolName ?? "")}</td>
          <td class="text-muted" style="font-size:.8125rem">${logDetails(e)}</td>
        </tr>`).join("")}
      </tbody>
    </table></div>
    <p class="text-muted" style="font-size:.8125rem;margin-top:.5rem">Die letzten 100 Einträge.</p>`;
}

// ── Seite ──

export async function render(el: HTMLElement) {
  pageEl = el;
  search = "";
  el.innerHTML = `
    <div class="page-header">
      <div>
        <h1>Schulträger</h1>
        <div style="font-size:.875rem;color:var(--gray-500);margin-top:.25rem">Plattform-Verwaltung. Schulträger, ihre Schulen und Haupt-Trägeradmins</div>
      </div>
    </div>
    <div class="filter-bar" style="margin-bottom:1rem"><input id="tr-admin-search" type="search" placeholder="Träger, Schule, Code oder E-Mail suchen …" /></div>
    <div id="tr-admin-list" class="carrier-org-list"></div>`;

  el.querySelector("#tr-admin-search")!.addEventListener("input", (e) => {
    search = (e.target as HTMLInputElement).value.trim().toLowerCase();
    renderList();
  });
  el.querySelector("#tr-admin-list")!.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const details = t.closest<HTMLButtonElement>("[data-details]");
    if (details) { openDetails(Number(details.dataset.details)); return; }
    const setFounder = t.closest<HTMLButtonElement>("[data-set-founder]");
    if (setFounder) {
      const c = carriers.find(x => x.id === Number(setFounder.dataset.setFounder));
      if (c) openSetFounder(c, reload);
    }
  });

  try { await reload(); }
  catch (err: any) {
    el.querySelector("#tr-admin-list")!.innerHTML = `<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
  }
}
